/**
 * Presentation identity for the existing shared portal/admin artifact.
 * This is not a tenant selector: Cognito, API routing and record ownership
 * must never be inferred from these values.
 */
export type BrandId = 'geauxfile' | 'eleet' | 'neutral';
export type AppSurface = 'portal' | 'admin';

export interface AppBrand {
  id: BrandId;
  companyName: string;
  legalName: string;
  shortName: string;
  websiteLabel: string;
  supportEmail: string;
  supportPhone: string;
  websiteUrl: string;
  portalUrl: string;
  termsUrl: string;
  privacyUrl: string;
  acceptableUseUrl: string;
  logoUrl: string;
  logoMarkUrl: string;
  faviconUrl: string;
  appleIconUrl: string;
  manifestUrl: string;
  portalTitle: string;
  adminTitle: string;
  description: string;
  themeColor: string;
  tagline: string;
}

export const BRANDS: Readonly<Record<BrandId, Readonly<AppBrand>>> = Object.freeze({
  geauxfile: Object.freeze({
    id: 'geauxfile',
    companyName: 'Geaux File LLC',
    legalName: 'Geaux File LLC',
    shortName: 'Geaux File',
    websiteLabel: 'geauxfile.com',
    supportEmail: 'agent@geauxfile.com',
    supportPhone: '855-596-0815',
    websiteUrl: 'https://geauxfile.com',
    portalUrl: 'https://portal.geauxfile.com',
    termsUrl: 'https://geauxfile.com/terms',
    privacyUrl: 'https://geauxfile.com/privacy',
    acceptableUseUrl: '',
    logoUrl: '/logo-geauxfile.svg',
    logoMarkUrl: '/logo-geauxfile-mark.svg',
    faviconUrl: '/logo-geauxfile-mark.svg',
    appleIconUrl: '/apple-touch-icon-geauxfile.png',
    manifestUrl: '/manifest-geauxfile.webmanifest',
    portalTitle: 'Geaux File Customer Portal',
    adminTitle: 'Geaux File LLC Admin',
    description: 'Geaux File LLC customer portal for registered-agent services',
    themeColor: '#002D62',
    tagline: 'Registered agent & compliance for the Gulf South',
  }),
  // Preserve the legacy presentation only on its own domain. No API or auth endpoints here.
  eleet: Object.freeze({
    id: 'eleet',
    companyName: 'Eleet AI',
    legalName: 'Eleet AI Inc.',
    shortName: 'eleet.ai',
    websiteLabel: 'eleet.ai',
    supportEmail: 'support@eleet.ai',
    supportPhone: '(888) 792-0467',
    websiteUrl: 'https://eleet.ai',
    portalUrl: 'https://app.eleet.ai',
    termsUrl: 'https://eleet.ai/terms',
    privacyUrl: 'https://eleet.ai/privacy',
    acceptableUseUrl: 'https://eleet.ai/acceptable-use',
    logoUrl: '/logo-full.svg',
    logoMarkUrl: '/logo-mark-nav.png',
    faviconUrl: '/favicon.svg',
    appleIconUrl: '/apple-touch-icon.png',
    manifestUrl: '/manifest-eleet.webmanifest',
    portalTitle: 'ELEET.AI Mission Control',
    adminTitle: 'ELEET.AI Admin',
    description: 'AI-operated registered agent services for Louisiana, Mississippi, and Alabama',
    themeColor: '#18181B',
    tagline: 'AI-operated registered agent services',
  }),
  neutral: Object.freeze({
    id: 'neutral',
    companyName: 'Service Portal',
    legalName: '',
    shortName: 'Service Portal',
    websiteLabel: 'Home',
    supportEmail: '',
    supportPhone: '',
    websiteUrl: '/',
    portalUrl: '/',
    termsUrl: '',
    privacyUrl: '',
    acceptableUseUrl: '',
    logoUrl: '/logo-neutral.svg',
    logoMarkUrl: '/logo-neutral.svg',
    faviconUrl: '/logo-neutral.svg',
    appleIconUrl: '',
    manifestUrl: '',
    portalTitle: 'Service Portal',
    adminTitle: 'Service Administration',
    description: 'Service portal',
    themeColor: '#334155',
    tagline: '',
  }),
});

/** Only a hostname is accepted, never a URL, query override or storage value. */
export function resolveBrandId(value: unknown): BrandId {
  if (typeof value !== 'string') return 'neutral';
  const host = value.toLowerCase().replace(/\.$/, '');
  if (!/^[a-z0-9]+(?:[a-z0-9.-]*[a-z0-9])?$/.test(host) || host.includes('..')) {
    return 'neutral';
  }
  if (host === 'geauxfile.com' || host.endsWith('.geauxfile.com')) return 'geauxfile';
  if (host === 'eleet.ai' || host.endsWith('.eleet.ai')) return 'eleet';
  return 'neutral';
}

/** Return a complete, immutable presentation identity for a parsed hostname. */
export function resolveBrand(hostname: unknown): Readonly<AppBrand> {
  return BRANDS[resolveBrandId(hostname)];
}

/** Self-contained so Vite can inline this same function before React loads. */
export function applyBrandToDocument(
  doc: Document,
  brand: Readonly<AppBrand>,
  surface: AppSurface
): void {
  doc.documentElement.dataset.brand = brand.id;
  doc.documentElement.classList.toggle('dark', brand.id === 'eleet' && surface === 'portal');
  doc.title = surface === 'admin' ? brand.adminTitle : brand.portalTitle;

  for (const [name, content] of Object.entries({
    description: brand.description,
    'theme-color': brand.themeColor,
    'application-name': brand.shortName,
    'apple-mobile-web-app-title': brand.shortName,
    'msapplication-TileColor': brand.themeColor,
  })) {
    let meta = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!meta) {
      meta = doc.createElement('meta');
      meta.name = name;
      doc.head.appendChild(meta);
    }
    meta.content = content;
  }

  // Remove inherited icon candidates too: browsers choose between all rel=icon links.
  doc
    .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"], link[rel="manifest"]')
    .forEach(link => link.remove());
  for (const [rel, href] of [
    ['icon', brand.faviconUrl],
    ['apple-touch-icon', surface === 'portal' ? brand.appleIconUrl : ''],
    ['manifest', surface === 'portal' ? brand.manifestUrl : ''],
  ]) {
    if (!rel || !href) continue;
    const link = doc.createElement('link');
    link.rel = rel;
    link.href = href;
    if (rel === 'icon') link.type = 'image/svg+xml';
    doc.head.appendChild(link);
  }
}
